<template>
  <LoadingWrapper :state="loadingStatus">
    <SideBarLayoutContent>
      <template #top-bar>
        <BackButton backup-route-name="feedbacks" class="mr-2 -ml-2" />
        <div class="flex grow items-center gap-2">
          <h2 v-if="surveyMemberFeedback.title" class="text-on-surface text-headline-large">
            {{ surveyMemberFeedback.title }}
          </h2>
          <TimeAgo
            class="text-body-large text-on-surface-variant ml-2"
            :date="surveyMemberFeedback.createdAt"
          />
        </div>

        <div class="flex shrink-0 items-center">
          <div class="text-label-large mr-3 flex shrink-0 items-center">
            <span v-if="surveyMemberFeedback.isSelf">{{ $t('common.selfFeedback') }}</span>
            <template v-else>
              <div v-if="surveyMemberFeedback.member" class="flex flex-col">
                <span>
                  {{
                    surveyMemberFeedback.isReceiver ? `${$t('common.to')}` : `${$t('common.from')}`
                  }}
                  {{ memberAbbreviatedName(surveyMemberFeedback.member) }}
                </span>
                <span class="text-body-medium text-on-surface-variant">{{ perspectiveName }}</span>
              </div>
              <template v-else>
                {{
                  surveyMemberFeedback.isReceiver ? `${$t('common.to')}` : `${$t('common.from')}`
                }}
                {{ perspectiveName }}
              </template>
            </template>
          </div>
          <SelfFeedbackAvatarFlow
            v-if="surveyMemberFeedback.isSelf"
            :member="surveyMemberFeedback.member!"
          />
          <div v-else class="flex flex-row items-center">
            <BaseMemberAvatar
              v-if="surveyMemberFeedback.isSender && surveyMemberFeedback.member"
              :member="surveyMemberFeedback.isSender ? surveyMemberFeedback.member! : user"
              class="z-10"
            />
            <BaseMemberAvatar
              v-else-if="!surveyMemberFeedback.isSender"
              :member="user"
              class="z-10"
            />
            <div
              v-else-if="surveyMemberFeedback.isSender && !surveyMemberFeedback.member"
              :member="user"
              class="bg-surface-container z-10 flex h-10 w-10 items-center justify-center rounded-full"
            >
              <BaseIcon size="20">{{ iconForPerspective(FeedbackPerspective.External) }}</BaseIcon>
            </div>
            <div class="relative flex h-10 w-8 items-center justify-center">
              <FeedbackFlow direction="right" class="absolute! z-0 h-full w-12" />
              <BaseIcon class="text-secondary z-10"> arrow_right_alt </BaseIcon>
            </div>
            <BaseMemberAvatar
              :member="surveyMemberFeedback.isReceiver ? surveyMemberFeedback.member! : user"
              class="z-10"
            />
          </div>
          <BaseMenu
            v-if="isSelfFeedback && surveyMemberFeedback.origin === 'personal'"
            size="M"
            width="narrow"
            :items="[
              {
                id: 'delete',
                title: $t('common.delete'),
                destructive: true,
                leadingIcon: 'delete',
                callback: openDeleteDialog,
              },
            ]"
            v-slot="{ open }"
          >
            <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
          </BaseMenu>
        </div>
      </template>
      <template #default>
        <section class="relative flex flex-col pb-10">
          <ul class="flex flex-col gap-8">
            <li
              v-for="feedbackTopic in surveyMemberFeedback.feedbackTopics"
              :key="feedbackTopic.id"
            >
              <h2 class="text-title-large text-on-surface-variant mb-1">
                {{ feedbackTopic.title }}
              </h2>
              <ul class="flex flex-col gap-0.5">
                <template
                  v-for="questionGroup in feedbackTopic.questionGroups"
                  :key="questionGroup.id"
                >
                  <li
                    v-for="(question, index) in questionGroup.questions"
                    :key="question.id"
                    class="first:[&>div]:rounded-t-large last:[&>div]:rounded-b-large @container"
                  >
                    <div
                      class="rounded-extra-small bg-surface-container flex flex-col gap-2 p-4 @xl:flex-row @xl:gap-0"
                    >
                      <h3 class="text-body-large w-full shrink-0 @xl:w-1/2">
                        {{ question.content }}
                      </h3>
                      <div class="flex flex-col gap-2 @xl:w-1/2">
                        <div
                          v-if="getFeedbackValue(question.id, feedbackTopic) != undefined"
                          class="-mt-1 w-full"
                        >
                          <PerspectiveValueBar
                            hide-labels
                            hide-number-of-feedbacks
                            :perspectives="
                              valueBarPerspectives(
                                feedbackTopic,
                                getFeedbackValue(question.id, feedbackTopic) ?? null,
                              )
                            "
                            :topic-perspectives="perspectivesFromTopic(feedbackTopic)"
                            :animation-delay="index * 40"
                          />
                        </div>
                        <div v-if="getFeedbackText(question.id, feedbackTopic)">
                          <p class="text-body-medium text-primary italic">
                            <WysiwygTextFieldReadOnly
                              :value="getFeedbackText(question.id, feedbackTopic)!"
                            />
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                </template>
              </ul>
            </li>
          </ul>
        </section>
      </template>
    </SideBarLayoutContent>
    <DeleteSelfFeedbackDialog
      :is-open="isDeleteDialogOpen"
      @close="closeDeleteDialog"
      @delete="confirmDeleteFeedback"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useSurveyMemberFeedback } from '../composables/useSurveyMemberFeedback'
  import SideBarLayoutContent from '@/core/components/layouts/SideBarLayoutContent.vue'
  import TimeAgo from '@/core/components/base/time-ago/TimeAgo.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BackButton from '@/core/components/BackButton.vue'
  import PerspectiveValueBar from '@/core/components/analytics/question-list/PerspectiveValueBar.vue'
  import FeedbackFlow from '@/core/components/feedback-flow/FeedbackFlow.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import { useUserStore } from '@/stores/user.store'
  import WysiwygTextFieldReadOnly from '@/core/components/base/wysiwyg/WysiwygTextFieldReadOnly.vue'
  import type { JSONContent } from '@tiptap/vue-3'
  import { FeedbackPerspective, iconForPerspective } from '@/core/models/feedback-perspective'
  import { perspectivesFromTopic } from '@/core/models/perspective'
  import { toValueBarPerspectives } from '@/core/models/perspective-analysis'
  import { memberAbbreviatedName } from '@/core/models/member/member-name'
  import type { Perspective } from '@/core/models/perspective'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import SelfFeedbackAvatarFlow from '@/core/components/feedback-flow/SelfFeedbackAvatarFlow.vue'
  import DeleteSelfFeedbackDialog from '../components/DeleteSelfFeedbackDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { computed } from 'vue'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label.ts'

  const { surveyMemberFeedback, loadingStatus, deleteSelfFeedback } = useSurveyMemberFeedback()

  const { user } = useUserStore()

  const valueBarPerspectives = (
    feedbackTopic: {
      perspectives: ReadonlyArray<Perspective>
      includeSelf: boolean
    },
    average: number | null,
  ) => {
    const topicPerspectives = perspectivesFromTopic(feedbackTopic)
    const perspective = topicPerspectives.find(
      (p) => p.key === surveyMemberFeedback.value.perspectiveKey,
    )
    if (!perspective) {
      return []
    }
    return toValueBarPerspectives(
      [
        {
          perspective,
          average,
          numberOfFeedbacks: 1,
        },
      ],
      topicPerspectives,
    )
  }

  const {
    isOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()

  const getFeedbackValue = (
    questionId: string,
    feedbackTopic: { feedbacks: ReadonlyArray<{ value: number | null; questionId: string }> },
  ) => {
    return feedbackTopic.feedbacks.find((feedback) => feedback.questionId === questionId)?.value
  }

  const getFeedbackText = (
    questionId: string,
    feedbackTopic: { feedbacks: ReadonlyArray<{ text: JSONContent | null; questionId: string }> },
  ) => {
    return feedbackTopic.feedbacks.find((feedback) => feedback.questionId === questionId)?.text
  }

  const isSelfFeedback = computed(() => {
    return surveyMemberFeedback.value.isSender && surveyMemberFeedback.value.isReceiver
  })

  const confirmDeleteFeedback = async () => {
    closeDeleteDialog()
    await deleteSelfFeedback()
  }

  const perspectiveName = computed(() => {
    return resolvePerspectiveLabel(
      surveyMemberFeedback.value.perspectiveKey,
      surveyMemberFeedback.value.feedbackTopics.flatMap((feedbackTopic) => {
        return feedbackTopic.perspectives
      }),
      {
        count: 1,
      },
    )
  })
</script>
